import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/pl';

dayjs.extend(customParseFormat);
dayjs.locale('pl');

/**
 * Format time for mobile display (shorter format)
 */
export const formatMobileTime = (time, use24Hour = true) => {
    const dayjsTime = dayjs(time);

    if (use24Hour) {
        return dayjsTime.format('HH:mm');
    } else {
        const formatted = dayjsTime.format('h:mm A');
        // Make AM/PM smaller or abbreviated
        return formatted.replace(' AM', 'a').replace(' PM', 'p');
    }
};

/**
 * Format date for mobile headers
 */
export const formatMobileDate = (date, variant = 'short') => {
    const dayjsDate = dayjs(date);

    switch (variant) {
        case 'short':
            return dayjsDate.format('DD MMM'); // "15 Sty"
        case 'medium':
            return dayjsDate.format('DD MMMM'); // "15 Stycznia"
        case 'long':
            return dayjsDate.format('dddd, DD MMMM'); // "Poniedziałek, 15 Stycznia"
        case 'weekday':
            return dayjsDate.format('ddd'); // "Pon"
        default:
            return dayjsDate.format('DD.MM');
    }
};

/**
 * Get relative time for mobile (today, tomorrow, etc.)
 */
export const getMobileRelativeTime = (date) => {
    const dayjsDate = dayjs(date);
    const today = dayjs();
    const tomorrow = today.add(1, 'day');
    const yesterday = today.subtract(1, 'day');

    if (dayjsDate.isSame(today, 'day')) {
        return 'Dzisiaj';
    } else if (dayjsDate.isSame(tomorrow, 'day')) {
        return 'Jutro';
    } else if (dayjsDate.isSame(yesterday, 'day')) {
        return 'Wczoraj';
    } else if (dayjsDate.isSame(today, 'week')) {
        return dayjsDate.format('dddd'); // Day name for current week
    } else {
        return dayjsDate.format('DD MMM');
    }
};

/**
 * Format time range for mobile event display
 */
export const formatMobileTimeRange = (startTime, endTime, includeDate = false) => {
    const start = dayjs(startTime);
    const end = dayjs(endTime);

    if (includeDate && !start.isSame(end, 'day')) {
        // Multi-day event
        return `${start.format('DD MMM HH:mm')} - ${end.format('DD MMM HH:mm')}`;
    } else if (includeDate) {
        // Same day with date
        return `${start.format('DD MMM')} • ${start.format('HH:mm')} - ${end.format('HH:mm')}`;
    } else {
        // Just time range
        return `${start.format('HH:mm')} - ${end.format('HH:mm')}`;
    }
};

/**
 * Parse time input from mobile time picker
 */
export const parseMobileTimeInput = (timeString, referenceDate = dayjs()) => {
    // Handle various input formats
    const formats = ['HH:mm', 'H:mm', 'HH', 'H'];

    for (const format of formats) {
        const parsed = dayjs(timeString, format);
        if (parsed.isValid()) {
            return referenceDate
                .hour(parsed.hour())
                .minute(parsed.minute())
                .second(0)
                .millisecond(0);
        }
    }

    return null;
};

/**
 * Generate time slots for mobile picker
 */
export const generateMobileTimeSlots = (startTime, endTime, interval = 15, format = 'HH:mm') => {
    const slots = [];
    let current = dayjs(startTime, 'HH:mm');
    const end = dayjs(endTime, 'HH:mm');

    while (current.isBefore(end) || current.isSame(end)) {
        slots.push({
            value: current.format('HH:mm'),
            label: current.format(format),
            hour: current.hour(),
            minute: current.minute()
        });
        current = current.add(interval, 'minute');
    }

    return slots;
};

/**
 * Format duration for mobile display (compact)
 */
export const formatMobileDuration = (minutes) => {
    if (minutes < 60) {
        return `${minutes}m`;
    } else if (minutes === 60) {
        return '1h';
    } else if (minutes % 60 === 0) {
        return `${Math.floor(minutes / 60)}h`;
    } else {
        return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
    }
};

/**
 * Get time ago for mobile (notifications, updates)
 */
export const getMobileTimeAgo = (date) => {
    const now = dayjs();
    const past = dayjs(date);
    const diffMinutes = now.diff(past, 'minute');
    const diffHours = now.diff(past, 'hour');
    const diffDays = now.diff(past, 'day');

    if (diffMinutes < 1) {
        return 'Teraz';
    } else if (diffMinutes < 60) {
        return `${diffMinutes} min temu`;
    } else if (diffHours < 24) {
        return `${diffHours} godz temu`;
    } else if (diffDays < 7) {
        return `${diffDays} dni temu`;
    } else {
        return past.format('DD MMM');
    }
};

/**
 * Format working hours for mobile display
 */
export const formatMobileWorkingHours = (openingTime, closingTime) => {
    return `${openingTime} - ${closingTime}`;
};

/**
 * Check if time slot is within working hours
 */
export const isWithinMobileWorkingHours = (time, openingTime, closingTime) => {
    const slotTime = dayjs(time);
    const dayStart = slotTime.startOf('day');
    const opening = dayStart.add(dayjs(openingTime, 'HH:mm').hour(), 'hour')
        .add(dayjs(openingTime, 'HH:mm').minute(), 'minute');
    const closing = dayStart.add(dayjs(closingTime, 'HH:mm').hour(), 'hour')
        .add(dayjs(closingTime, 'HH:mm').minute(), 'minute');

    return slotTime.isSameOrAfter(opening) && slotTime.isSameOrBefore(closing);
};